import React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    LinearProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha } from '@mui/material/styles';
import { NOTHING_MATCHED_OUTCOME } from '../../utils/watchlistImportOutcome';
import {
    OVERALL_STATUS_FAILED,
    OVERALL_STATUS_PARTIAL,
    OVERALL_STATUS_RUNNING,
    OVERALL_STATUS_SUCCESS,
    deriveSecurityOperationOverallStatus,
    deriveSecurityOperationProgress,
    shouldShowSecurityOperationBanner,
} from '../../utils/securityOperationStatus';
import {
    getWatchlistMsiTone,
} from '../../styles/watchlistMsiStyles';
import { secondaryModalSx } from '../../styles/secondaryMsiStyles';
import { normalizeWatchlistLifecycle } from '../../utils/watchlistLifecycle';
import { cancelWatchlistUpdate } from '../../utils/ApiCall';
import {
    WATCHLIST_HISTORY_OPEN_EVENT,
    WATCHLIST_REVISION_PROGRESS_EVENT,
    scopeSecurityOperationEventDetail,
} from '../../utils/securityResolutionEvents';
import { useSecurityOperationContext } from '../../context/SecurityOperationContext';

const STATUS_ICON = {
    primary: AutorenewRoundedIcon,
    success: CheckCircleRoundedIcon,
    info: InfoOutlinedIcon,
    warning: ErrorOutlineRoundedIcon,
    error: ErrorOutlineRoundedIcon,
    cancelled: CloseRoundedIcon,
};

const UPLOAD_FALLBACK_PROGRESS = 8;

const PHASE_LABELS = {
    mutation: 'Saving changes',
    identity_resolution: 'Checking securities',
    factor_coverage: 'Checking factor coverage',
    market_registration: 'Refreshing market data',
    factor_matrix_refresh: 'Refreshing factor inputs',
    surface_refresh: 'Updating the dashboard',
    removal_review: 'Review required',
};

function operationBaseCopy() {
    return {
        active: 'Preparing watchlist',
        success: 'Watchlist ready',
        partial: 'Watchlist updated',
        failed: 'Watchlist update failed',
        fallbackDetail: 'Uploading watchlist file',
        helper: 'Checking securities and refreshing watchlist data.',
    };
}

function activePhaseLabel(statusDoc) {
    const phases = statusDoc?.phases && typeof statusDoc.phases === 'object'
        ? statusDoc.phases
        : {};
    // A downstream phase may already be queued while the current phase is
    // running. Prefer the actual runner so the label never jumps ahead of the
    // operation's authoritative progress.
    const phaseEntries = Object.entries(PHASE_LABELS);
    const activeEntry = phaseEntries.find(([phase]) => phases[phase]?.status === 'running')
        || phaseEntries.find(([phase]) => phases[phase]?.status === 'queued');
    if (activeEntry) return activeEntry[1];
    if (PHASE_LABELS[statusDoc?.phase]) return PHASE_LABELS[statusDoc.phase];
    return 'Refreshing watchlist data';
}

function getPresentation(overall, statusDoc) {
    const copy = operationBaseCopy();
    if (statusDoc?.outcome === NOTHING_MATCHED_OUTCOME) {
        // A completed import that matched nothing: a plain outcome, not a failure.
        return {
            label: 'Nothing added',
            displayText: 'Nothing added - Watchlist unchanged',
            helper: statusDoc.message,
            color: 'info',
        };
    }
    if (statusDoc?.cancelled) {
        return {
            label: 'Watchlist update cancelled',
            displayText: 'Watchlist update cancelled',
            helper: 'Your current watchlist was not changed.',
            color: 'cancelled',
        };
    }
    if (
        overall === OVERALL_STATUS_RUNNING
        && statusDoc?.phase === 'removal_review'
    ) {
        return {
            label: 'Factor data update - review required',
            displayText: 'Factor data update - Review changes',
            detail: 'Review required',
            helper: 'The new factor data changes the securities in your Watchlist. Nothing is published until you review those changes.',
            color: 'warning',
        };
    }
    switch (overall) {
        case OVERALL_STATUS_SUCCESS:
            return {
                label: copy.success,
                displayText: copy.success,
                helper: 'Watchlist membership and app data are current.',
                color: 'success',
            };
        case OVERALL_STATUS_PARTIAL:
            return {
                label: copy.partial,
                displayText: `${copy.partial} - Some data needs attention`,
                helper: 'Available securities were updated; review notifications for details.',
                color: 'success',
            };
        case OVERALL_STATUS_FAILED:
            return {
                label: copy.failed,
                displayText: copy.failed,
                helper: statusDoc?.operationType === 'watchlist_update'
                    ? 'Your active Watchlist was not changed.'
                    : 'Review notifications for the securities that need attention.',
                color: 'error',
            };
        default:
            {
                const detail = activePhaseLabel(statusDoc);
                return {
                    label: copy.active,
                    displayText: detail ? `${copy.active} - ${detail}` : copy.active,
                    detail,
                    helper: copy.helper,
                    color: 'primary',
                };
            }
    }
}

function getFallbackPresentation() {
    const copy = operationBaseCopy();
    return {
        label: copy.active,
        detail: copy.fallbackDetail,
        displayText: `${copy.active} - ${copy.fallbackDetail}`,
        helper: copy.helper,
        color: 'primary',
    };
}

function statusTone(color) {
    if (color === 'success') return 'success';
    if (color === 'warning') return 'warning';
    if (color === 'error') return 'danger';
    return 'info';
}

function getStatusColor(color, theme) {
    const msiTone = getWatchlistMsiTone(theme, statusTone(color));
    if (msiTone) return msiTone.foreground;
    if (color === 'success') return 'rgb(var(--c-positive))';
    if (color === 'warning') return theme.palette.warning.main;
    if (color === 'error') return 'rgb(var(--c-negative))';
    return theme.palette.primary.main;
}

function getStatusColorWithAlpha(color, alphaValue, theme) {
    const msiTone = getWatchlistMsiTone(theme, statusTone(color));
    if (msiTone) {
        return `color-mix(in srgb, ${msiTone.foreground} ${alphaValue * 100}%, transparent)`;
    }
    if (color === 'success') return `rgb(var(--c-positive) / ${alphaValue})`;
    if (color === 'warning') return alpha(theme.palette.warning.main, alphaValue);
    if (color === 'error') return `rgb(var(--c-negative) / ${alphaValue})`;
    return alpha(theme.palette.primary.main, alphaValue);
}

function getProgressGradient(color, theme) {
    const msiTone = getWatchlistMsiTone(theme, statusTone(color));
    if (msiTone) {
        return msiTone.foreground;
    }
    if (color === 'success') {
        return 'linear-gradient(90deg, rgb(var(--c-positive) / 0.86), rgb(var(--c-positive)))';
    }
    if (color === 'error') {
        return 'linear-gradient(90deg, rgb(var(--c-negative) / 0.86), rgb(var(--c-negative)))';
    }
    if (color === 'warning') return theme.palette.warning.main;
    const start = theme.palette.primary.dark || theme.palette.primary.main;
    const end = theme.palette.primary.light || theme.palette.primary.main;
    return `linear-gradient(90deg, ${start}, ${end})`;
}

function tooltipTitle({ presentation, statusDoc }) {
    const summary = statusDoc?.summary && typeof statusDoc.summary === 'object'
        ? statusDoc.summary
        : null;
    // Submitted rows are not Watchlist rows when the import added nothing.
    const rowCount = statusDoc?.outcome === NOTHING_MATCHED_OUTCOME
        ? Number.NaN
        : Number(summary?.row_count ?? summary?.active_rows);
    return (
        <Box sx={{ maxWidth: 300 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                {presentation.label}
            </Typography>
            {presentation.detail && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                    {presentation.detail}
                </Typography>
            )}
            {presentation.helper && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                    {presentation.helper}
                </Typography>
            )}
            {Number.isFinite(rowCount) && rowCount >= 0 && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {rowCount} watchlist row{rowCount === 1 ? '' : 's'}
                </Typography>
            )}
        </Box>
    );
}

export default function SecurityOperationProgress({
    statusDoc,
    uploading = false,
    onOpenRemovalReview,
    contextUpdate = null,
    compact = false,
}) {
    const {
        readState,
        sessionScopeToken,
        pendingReveal,
        acknowledgeReveal,
    } = useSecurityOperationContext();
    const [cancelTargetRevisionId, setCancelTargetRevisionId] = React.useState(null);
    const [cancelBusy, setCancelBusy] = React.useState(false);
    const [cancelError, setCancelError] = React.useState('');
    const cancelRequestInFlightRef = React.useRef(false);
    const contextProgress = contextUpdate?.sessionScopeToken === sessionScopeToken
        ? contextUpdate : null;
    const revealProgressRef = React.useRef(null);
    const readyForReveal = Boolean(pendingReveal
        && pendingReveal.sessionScopeToken === sessionScopeToken
        && typeof pendingReveal.isCurrent === 'function'
        && typeof acknowledgeReveal === 'function'
        && (!statusDoc?.revisionId || statusDoc.revisionId === pendingReveal.revisionId)
        && (!contextProgress?.revisionId || contextProgress.revisionId === pendingReveal.revisionId)
        && pendingReveal.isCurrent());
    // Only a laid-out, visible100 view may acknowledge this exact attempt.
    // Two animation frames leave an intervening paint; hidden tabs wait.
    React.useEffect(() => {
        if (pendingReveal?.sessionScopeToken === sessionScopeToken
            && typeof pendingReveal.isCurrent === 'function' && typeof acknowledgeReveal === 'function'
            && !pendingReveal.isCurrent()) {
            acknowledgeReveal(pendingReveal);
            return undefined;
        }
        if (!readyForReveal || typeof window.requestAnimationFrame !== 'function') return undefined;
        let disposed = false;
        let firstFrame = null;
        let secondFrame = null;
        const cancelFrames = () => {
            if (firstFrame != null) window.cancelAnimationFrame(firstFrame);
            if (secondFrame != null) window.cancelAnimationFrame(secondFrame);
            firstFrame = null;
            secondFrame = null;
        };
        const current = () => {
            if (disposed) return false;
            if (!pendingReveal.isCurrent()) {
                acknowledgeReveal(pendingReveal);
                return false;
            }
            const element = revealProgressRef.current;
            const bar = element?.querySelector('[role="progressbar"]');
            return document.visibilityState === 'visible' && element?.isConnected
                && bar?.getAttribute('aria-valuenow') === '100'
                && bar.getClientRects().length > 0
                && window.getComputedStyle(bar).visibility !== 'hidden';
        };
        const start = () => {
            cancelFrames();
            if (!current()) return;
            firstFrame = window.requestAnimationFrame(() => {
                firstFrame = null;
                if (!current()) return;
                secondFrame = window.requestAnimationFrame(() => {
                    secondFrame = null;
                    if (current()) acknowledgeReveal(pendingReveal);
                });
            });
        };
        document.addEventListener('visibilitychange', start);
        window.addEventListener('resize', start);
        // Persistent route views can acquire layout without a prop change.
        // Observe only this pending progress bar, not unrelated renders.
        const observer = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(start) : null;
        const bar = revealProgressRef.current?.querySelector('[role="progressbar"]');
        if (bar) observer?.observe(bar);
        start();
        return () => {
            disposed = true;
            cancelFrames();
            document.removeEventListener('visibilitychange', start);
            window.removeEventListener('resize', start);
            observer?.disconnect();
        };
    }, [pendingReveal, readyForReveal, acknowledgeReveal, sessionScopeToken]);

    const hasRenderableStatus = Boolean(
        statusDoc
        && statusDoc.found
        && (!contextProgress || (contextProgress.revisionId
            && contextProgress.revisionId === statusDoc.revisionId))
        && shouldShowSecurityOperationBanner(statusDoc),
    );
    const renderedStatusDuringBusyRef = React.useRef(false);
    if (!uploading) {
        renderedStatusDuringBusyRef.current = false;
    } else if (hasRenderableStatus) {
        renderedStatusDuringBusyRef.current = true;
    }
    // `uploading` is also used while the dashboard reloads after the durable
    // parent operation reaches 100%. If that terminal sample settles before
    // the reload finishes, do not paint a second 8% file-upload bar.
    const useFallback = (
        uploading
        && !hasRenderableStatus
        && !renderedStatusDuringBusyRef.current
    );
    const readUnavailable = readState === 'unavailable';
    const statusOverall = hasRenderableStatus
        ? deriveSecurityOperationOverallStatus(statusDoc.phases, statusDoc.topLevelStatus)
        : null;
    // A fresh staging request has no durable progress yet. A completed parent
    // can also still be waiting for accepted UI/portfolio publication. Neither
    // has an honest remaining percentage, so use the same bar indeterminately.
    const contextPending = Boolean(contextProgress
        && (!hasRenderableStatus || (statusOverall === OVERALL_STATUS_SUCCESS
            && statusDoc.uiCommitComplete !== true)));
    const canCancel = Boolean(
        !readyForReveal
        && !useFallback
        && statusOverall === OVERALL_STATUS_RUNNING
        && statusDoc?.operationType === 'watchlist_update'
        && statusDoc?.phase !== 'removal_review'
        && statusDoc?.revisionId,
    );
    // A factor data update that changes the Watchlist's securities waits for its own
    // review in Watchlist History.
    const canReviewRemovals = Boolean(
        !useFallback
        && statusOverall === OVERALL_STATUS_RUNNING
        && statusDoc?.phase === 'removal_review'
        && statusDoc?.revisionId
    );
    const cancelDialogOpen = Boolean(
        cancelTargetRevisionId
        && canCancel
        && cancelTargetRevisionId === statusDoc?.revisionId
    );
    React.useEffect(() => {
        if (cancelTargetRevisionId && !cancelDialogOpen) {
            setCancelTargetRevisionId(null);
            setCancelError('');
        }
    }, [cancelDialogOpen, cancelTargetRevisionId]);
    if (!readyForReveal && !hasRenderableStatus && !useFallback && !readUnavailable && !contextPending) return null;

    const overall = readyForReveal || useFallback || contextPending || (!hasRenderableStatus && readUnavailable)
        ? OVERALL_STATUS_RUNNING
        : statusOverall;
    const progress = readyForReveal
        ? 100
        : contextPending
        ? null
        : useFallback
        ? UPLOAD_FALLBACK_PROGRESS
        : hasRenderableStatus
            ? deriveSecurityOperationProgress(statusDoc)
            : null;
    const basePresentation = useFallback || contextPending || (!hasRenderableStatus && readUnavailable)
        ? getFallbackPresentation()
        : getPresentation(overall, statusDoc);
    const presentation = contextProgress && overall === OVERALL_STATUS_RUNNING && !canReviewRemovals
        ? {
            ...basePresentation,
            label: 'Factor data update',
            displayText: contextProgress.message,
            helper: contextProgress.detail,
        }
        : basePresentation;
    const progressVariant = progress == null ? 'indeterminate' : 'determinate';
    const Icon = STATUS_ICON[presentation.color] || AutorenewRoundedIcon;
    const percentColor = (theme) => getStatusColor(presentation.color, theme);
    const handleCancelRequest = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (cancelBusy || !canCancel) return;
        setCancelError('');
        setCancelTargetRevisionId(statusDoc.revisionId);
    };
    const handleOpenRemovalReview = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!canReviewRemovals) return;
        const detail = scopeSecurityOperationEventDetail({
            revisionId: statusDoc.revisionId,
            revisionSeq: statusDoc.revisionSeq ?? null,
        }, sessionScopeToken);
        if (!detail) return;
        if (onOpenRemovalReview) {
            onOpenRemovalReview(detail);
            return;
        }
        window.dispatchEvent(new CustomEvent(WATCHLIST_HISTORY_OPEN_EVENT, { detail }));
    };

    const handleCancelDialogClose = () => {
        if (cancelBusy) return;
        setCancelTargetRevisionId(null);
        setCancelError('');
    };

    const handleCancelConfirm = async () => {
        if (
            cancelRequestInFlightRef.current
            || cancelBusy
            || !canCancel
            || cancelTargetRevisionId !== statusDoc?.revisionId
        ) return;
        cancelRequestInFlightRef.current = true;
        setCancelBusy(true);
        setCancelError('');
        try {
            const response = await cancelWatchlistUpdate({
                revisionId: cancelTargetRevisionId,
                attemptId: window.crypto?.randomUUID?.()
                    || `cancel-${Date.now()}`,
            });
            if (!response?.success) {
                setCancelError(response?.message || 'Cancellation failed.');
                return;
            }
            const lifecycle = normalizeWatchlistLifecycle(response);
            if (lifecycle.status !== 'cancelled' || lifecycle.phase !== 'complete') {
                setCancelError('Cancellation returned an invalid status. Refresh and try again.');
                return;
            }
            const detail = scopeSecurityOperationEventDetail({
                lifecycle,
                ui_commit_complete: false,
            }, sessionScopeToken);
            if (!detail) {
                setCancelError('Your session changed before cancellation completed. Refresh and try again.');
                return;
            }
            setCancelTargetRevisionId(null);
            window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_PROGRESS_EVENT, {
                detail,
            }));
        } catch (error) {
            setCancelError(error?.message || 'Cancellation failed.');
        } finally {
            cancelRequestInFlightRef.current = false;
            setCancelBusy(false);
        }
    };

    return (
        <>
            <Tooltip title={tooltipTitle({ presentation, statusDoc: hasRenderableStatus ? statusDoc : null })} arrow>
                <Box
                    ref={revealProgressRef}
                    data-testid="security-operation-progress"
                    data-ready-for-reveal={readyForReveal ? 'true' : undefined}
                    role={contextProgress ? 'status' : undefined}
                    aria-label={contextProgress ? 'Factor data update progress' : undefined}
                    data-overall-status={overall}
                    data-display-color={presentation.color}
                    data-read-state={readState}
                    sx={[
                        {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.9,
                            width: compact ? { xs: 210, sm: 300 } : { xs: '100%', sm: 440 },
                            maxWidth: '100%',
                            minWidth: compact ? 190 : { xs: 260, sm: 340 },
                            flexShrink: compact ? 1 : undefined,
                            px: 0.25,
                            py: 0.35,
                        },
                    ]}
            >
                <Box
                    data-testid="security-operation-icon"
                    sx={{
                        width: 21,
                        height: 21,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: percentColor,
                        '@keyframes watchlistOperationSpin': {
                            to: { transform: 'rotate(360deg)' },
                        },
                        '& svg': presentation.color === 'primary'
                            ? { animation: 'watchlistOperationSpin 1.8s linear infinite' }
                            : null,
                    }}
                >
                    <Icon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        data-testid="security-operation-progress-title"
                        variant="caption"
                        sx={{
                            color: 'text.primary',
                            display: 'block',
                            fontWeight: 600,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {presentation.displayText || presentation.label}
                    </Typography>
                    {readUnavailable && (
                        <Typography
                            data-testid="security-operation-read-unavailable"
                            variant="caption"
                            sx={{
                                color: 'warning.main',
                                display: 'block',
                                fontSize: '0.68rem',
                                lineHeight: 1.1,
                                mt: 0.25,
                            }}
                        >
                            Status temporarily unavailable — retrying
                        </Typography>
                    )}
                    <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                        <LinearProgress
                            aria-label={contextProgress ? 'Factor data update progress' : 'Watchlist update progress'}
                            variant={progressVariant}
                            value={progress ?? undefined}
                            sx={{
                                height: 3,
                                borderRadius: 99,
                                flex: 1,
                                bgcolor: (theme) => getStatusColorWithAlpha(presentation.color, 0.1, theme),
                                '& .MuiLinearProgress-bar': {
                                    transition: readyForReveal ? 'none' : undefined,
                                    borderRadius: 99,
                                    background: (theme) => getProgressGradient(presentation.color, theme),
                                    boxShadow: (theme) => (
                                        `0 0 8px ${getStatusColorWithAlpha(presentation.color, 0.32, theme)}`
                                    ),
                                },
                            }}
                        />
                        {progress != null && (
                            <Typography
                                variant="caption"
                                sx={{ color: percentColor, fontVariantNumeric: 'tabular-nums' }}
                            >
                                {Math.round(progress)}%
                            </Typography>
                        )}
                    </Box>
                </Box>
                {canCancel && (
                    <Tooltip title={cancelError || 'Cancel watchlist update'} arrow>
                        <span>
                            <IconButton
                                size="small"
                                aria-label="Cancel watchlist update"
                                disabled={cancelBusy}
                                onClick={handleCancelRequest}
                                sx={{ width: 26, height: 26, flexShrink: 0 }}
                            >
                                {cancelBusy
                                    ? <CircularProgress size={15} />
                                    : <CloseRoundedIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {canReviewRemovals && (
                    <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={handleOpenRemovalReview}
                        sx={{ flexShrink: 0, minWidth: 0, px: 1, textTransform: 'none' }}
                    >
                        Review changes
                    </Button>
                )}
                </Box>
            </Tooltip>
            <Dialog
                open={cancelDialogOpen}
                onClose={handleCancelDialogClose}
                maxWidth="xs"
                fullWidth
                aria-labelledby="cancel-watchlist-update-title"
                aria-describedby="cancel-watchlist-update-description"
                PaperProps={{ sx: secondaryModalSx }}
            >
                <DialogTitle id="cancel-watchlist-update-title">
                    Cancel watchlist update?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="cancel-watchlist-update-description">
                        Your current watchlist will remain unchanged.
                    </DialogContentText>
                    {cancelError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {cancelError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        autoFocus
                        onClick={handleCancelDialogClose}
                        disabled={cancelBusy}
                    >
                        Keep running
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleCancelConfirm}
                        disabled={cancelBusy}
                        startIcon={cancelBusy
                            ? <CircularProgress size={14} thickness={5} color="inherit" />
                            : null}
                    >
                        {cancelBusy ? 'Cancelling...' : 'Cancel update'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
